import { CloseOutlined, SettingOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { SmlAsset, SmlJsonQueryOrder } from "@src/types/intelligence-search";
import { Button, Card, InputNumber, Popover, Tooltip } from "antd";
import React from "react";
import { SearchQueryOrder } from "../search-query-order";

export type TemplateSearchOrderLimitValue = {
    orders?: SmlJsonQueryOrder[];
    limit?: number;
};
export type TemplateSearchOrderLimitOption = {
    label: string;
    value: string;
    meta?: SmlAsset;
};
export type TemplateSearchOrderLimitProps = {
    initValue?: TemplateSearchOrderLimitValue;
    onChange?: (value: TemplateSearchOrderLimitValue) => void;
    options: TemplateSearchOrderLimitOption[];
    renderLabelOption?: (option: TemplateSearchOrderLimitOption) => React.ReactNode;
    handleCancel?: () => void;
    handleOk?: (value: TemplateSearchOrderLimitValue) => void;
};
export const DEFAULT_LIMIT = 1000;
const DEFAULT_VALUE = {
    orders: [],
    limit: DEFAULT_LIMIT,
};
export const TemplateSearchOrderLimit: React.FC<TemplateSearchOrderLimitProps> = ({
    initValue,
    onChange,
    options,
    renderLabelOption,
    handleCancel,
    handleOk,
}) => {
    const [value, setValue] = React.useState<TemplateSearchOrderLimitValue>(DEFAULT_VALUE);
    const [open, setOpen] = React.useState(false);

    const orderOptions = React.useMemo(() => {
        return options.map((option) => ({
            key: option.value,
            label: renderLabelOption?.(option) ?? option.label,
            value: option.value,
            meta: option.meta,
        }));
    }, [options, renderLabelOption]);

    React.useEffect(() => {
        onChange?.(value);
    }, [value, onChange]);

    const _handleCancel = () => {
        setOpen(false);
        handleCancel?.();
    };

    const _handleOk = () => {
        setOpen(false);
        handleOk?.(value);
    };

    const isConfigChange =
        JSON.stringify([value.limit, value.orders]) !== JSON.stringify([initValue?.limit, initValue?.orders]);

    return (
        <>
            <Popover
                open={open}
                css={css`
                    .ant-popover-inner {
                        padding: 0px !important;
                    }
                `}
                onOpenChange={(open) => {
                    setOpen(open);

                    if (open) {
                        setValue(initValue ?? DEFAULT_VALUE);
                        return;
                    }
                }}
                trigger={"click"}
                placement="left"
                title={null}
                content={
                    <Card
                        css={css`
                            .ant-card-head {
                                background-color: #0050b3;
                                color: white;
                            }
                        `}
                        title="Config result listing"
                        extra={
                            <CloseOutlined
                                className="text-white"
                                onClick={() => {
                                    _handleCancel();
                                }}
                            />
                        }
                        actions={[
                            <Button
                                onClick={() => {
                                    _handleCancel();
                                }}
                            >
                                Cancel
                            </Button>,
                            <Button
                                disabled={!isConfigChange}
                                type="primary"
                                onClick={() => {
                                    _handleOk();
                                }}
                            >
                                OK
                            </Button>,
                        ]}
                    >
                        <div>
                            <div className="flex gap-4 justify-start items-center">
                                <span className="font-bold">Limit</span>
                                <InputNumber
                                    value={value.limit}
                                    min={1}
                                    max={10000}
                                    precision={0}
                                    onBlur={() => {
                                        if (!value.limit) {
                                            setValue({ ...value, limit: DEFAULT_LIMIT });
                                        }
                                    }}
                                    onChange={(_value) => {
                                        _value && setValue({ ...value, limit: _value });
                                    }}
                                />
                            </div>

                            <SearchQueryOrder
                                options={orderOptions}
                                value={value.orders}
                                onChange={(_value) => {
                                    setValue({ ...value, orders: _value });
                                }}
                            />
                        </div>
                    </Card>
                }
                getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
                destroyOnHidden
            >
                <Tooltip title="Config order and limit">
                    <Button icon={<SettingOutlined />} />
                </Tooltip>
            </Popover>
        </>
    );
};
